import { useCallback, useEffect, useMemo, useState } from 'react';
import { LifecycleStatusTag } from 'components/lifecycle-status-tag';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  Accordion,
  AccordionItem,
  Button,
  Dropdown,
  InlineLoading,
  InlineNotification,
  Tooltip,
} from '@carbon/react';
import { Add, Close, SubtractAlt, VerticalFold } from '@carbon/react/icons';
import {
  addSectionGridsAtom,
  focusedWellUwiAtom,
  sectionModeAtom,
  sectionOrderingAtom,
  sectionVerticesAtom,
  sectionWellsAtom,
  toggleFocusedWellAtom,
  toggleSectionWellAtom,
  knownGridsAtom,
  rememberGridsAtom,
  removeSectionGridAtom,
  sectionDatumGridIdAtom,
  sectionGridIdsAtom,
  selectCollectionAtom,
  settingsPanelOpenAtom,
  structureGridCollectionIdAtom,
  toggleDatumGridAtom,
} from './atoms';
import {
  useStructureGridCollection,
  useStructureGridCollections,
  type CollectionGrid,
  type CollectionSummary,
} from './use-structure-grid-collections';
import { useCrossSection } from './use-cross-section';
import { useCrossSectionRequest } from './use-cross-section-request';
import { AddGridsModal } from './add-grids-modal';
import { useIntersectingStructureGrids } from './use-intersecting-structure-grids';
import { SectionWellsList } from './section-wells-list';
import { WellPickerModal } from './well-picker-modal';
import { WELL_ORDERINGS } from './well-orderings';
import type { PickedWell, SectionWellRow } from './types';
import styles from './cross-section-settings-panel.module.scss';

/**
 * A row back down to just the pick. The row carries what the cut measured on top of it,
 * which the next request neither needs nor should be trusted to have.
 *
 * @param {SectionWellRow} row - The listed well.
 * @return {PickedWell} The pick.
 */
const asPicked = ({ uwi, lat, lon, name }: SectionWellRow): PickedWell => ({
  uwi,
  lat,
  lon,
  name,
});

/** What the well picker was opened to do, or null while it is shut. */
type PickerIntent =
  | { mode: 'add' }
  | { mode: 'replace'; well: SectionWellRow; position: number }
  | null;

/**
 * Grid identity and provenance, for the row's subtext: which surface it is, who
 * built it, and when.
 *
 * @param {CollectionGrid} grid - The grid.
 * @return {string} The subtext, with anything missing left out rather than blank.
 */
const gridProvenance = (grid: CollectionGrid): string =>
  [grid.gridSurface, grid.owner, grid.source].filter(Boolean).join(' - ');

/**
 * One collection row: its name, the Default pill where it applies, and a note where the
 * section passes through none of its grids. The list is filtered to crossed collections,
 * so that note reaches only the selected one — which is pinned whatever the line does,
 * and is worth flagging when the line has moved off it.
 *
 * @param {CollectionSummary | null} item - The collection, or null for the empty field.
 * @param {Set<number> | null} crossed - The collections the section passes through, or
 *   null where that is not yet known and nothing should be said either way.
 * @return {JSX.Element} The row.
 */
const renderCollection = (
  item: CollectionSummary | null,
  crossed: Set<number> | null
): JSX.Element => {
  if (!item) {
    return <span />;
  }
  return (
    <span className={styles.collectionOption}>
      <span className={styles.collectionName}>{item.collectionName}</span>
      {item.isDefault && <LifecycleStatusTag status="default" />}
      {crossed !== null && !crossed.has(item.id) && (
        <span className={styles.collectionNote}>Not crossed</span>
      )}
    </span>
  );
};

/**
 * Everything that changes what the section IS: the collection it samples, the grids
 * on it, and which of them the section hangs from.
 *
 * The split is the rule — this panel edits the stack, and the map's display rail
 * holds how it looks. That is why there is no colour swatch on these rows.
 *
 * @return {JSX.Element | null} The panel, or nothing while it is closed.
 */
export const CrossSectionSettingsPanel = (): JSX.Element | null => {
  const open = useAtomValue(settingsPanelOpenAtom);
  const setOpen = useSetAtom(settingsPanelOpenAtom);
  const collectionId = useAtomValue(structureGridCollectionIdAtom);
  const gridIds = useAtomValue(sectionGridIdsAtom);
  const datumGridId = useAtomValue(sectionDatumGridIdAtom);
  const selectCollection = useSetAtom(selectCollectionAtom);
  const removeGrid = useSetAtom(removeSectionGridAtom);
  const addGrids = useSetAtom(addSectionGridsAtom);
  const toggleDatum = useSetAtom(toggleDatumGridAtom);

  const mode = useAtomValue(sectionModeAtom);
  const [picked, setPicked] = useAtom(sectionWellsAtom);
  const removeWell = useSetAtom(toggleSectionWellAtom);

  // The cut, read from the same cache the drawer fills.
  const { request, generation } = useCrossSectionRequest();
  const { data } = useCrossSection(request, generation);
  const [ordering, setOrdering] = useAtom(sectionOrderingAtom);

  // What the cut measured about each well, by uwi. Names and total depths come from
  // here whichever way the rows are ordered — only the order itself is in question.
  const measured = useMemo(() => {
    const byUwi = new Map<string, SectionWellRow>();
    (data?.subject.vertexLabels ?? []).forEach((vertex) => {
      if (vertex.uwi === null) {
        return;
      }
      byUwi.set(vertex.uwi, {
        uwi: vertex.uwi,
        lat: vertex.lat,
        lon: vertex.lon,
        name: vertex.name,
        // The hole's own length: its surface elevation down to the survey's toe.
        totalDepthFt:
          vertex.surfaceElevationFt !== null && vertex.tdTvdssFt !== null
            ? vertex.surfaceElevationFt - vertex.tdTvdssFt
            : null,
      });
    });
    return byUwi;
  }, [data?.subject.vertexLabels]);

  // Who decides the order decides these rows. Under an automatic ordering the backend
  // does and the rows follow the plane it built, keyed on the position number the plot
  // and map use. Under "As picked" the user does, so a dragged row stays put.
  const wells = useMemo<SectionWellRow[]>(() => {
    if (ordering === 'as_selected') {
      return picked.map((well) => {
        const row = measured.get(well.uwi);
        return {
          ...well,
          ...row,
          name: row?.name ?? well.name,
          totalDepthFt: row?.totalDepthFt ?? null,
        };
      });
    }
    const ordered = data?.subject.vertexLabels ?? [];
    if (ordered.length === 0) {
      return picked.map((well) => ({ ...well, totalDepthFt: null }));
    }
    return ordered.flatMap((vertex) => {
      if (vertex.uwi === null) {
        return [];
      }
      const row = measured.get(vertex.uwi);
      if (row === undefined) {
        return [];
      }
      const pick = picked.find((well) => well.uwi === vertex.uwi);
      return [{ ...row, name: row.name ?? pick?.name ?? null }];
    });
  }, [ordering, picked, measured, data?.subject.vertexLabels]);
  const focusedWellUwi = useAtomValue(focusedWellUwiAtom);
  const toggleFocus = useSetAtom(toggleFocusedWellAtom);
  const [gridsOpen, setGridsOpen] = useState(true);
  const [wellsOpen, setWellsOpen] = useState(true);
  const [addingGrids, setAddingGrids] = useState(false);
  // What the picker is for, or null while it is shut. Replace carries the well standing
  // down, because the footer names it and the commit needs its position.
  const [picking, setPicking] = useState<PickerIntent>(null);
  const drawnVertices = useAtomValue(sectionVerticesAtom);

  const {
    collections,
    loading: collectionsLoading,
    error: collectionsError,
  } = useStructureGridCollections(true);
  const { grids, loading: gridsLoading } =
    useStructureGridCollection(collectionId);

  const selected = useMemo(
    () =>
      collections.find((collection) => collection.id === collectionId) ?? null,
    [collections, collectionId]
  );

  // Choosing the default collection and putting its stack on the section happens in
  // `useCrossSectionBootstrap`, with the drawer: a section that stays blank until this
  // panel is opened is a section that looks broken.

  const known = useAtomValue(knownGridsAtom);
  const remember = useSetAtom(rememberGridsAtom);

  const onSection = useMemo(
    () =>
      gridIds
        .map(
          (id) =>
            grids.find((grid) => grid.structureGridId === id) ??
            known[String(id)]
        )
        .filter((grid): grid is CollectionGrid => grid !== undefined),
    [gridIds, grids, known]
  );

  /**
   * Commits a hand-made order. The rows are shown in whatever order the cut came back
   * in, so the edited list is committed exactly as it now reads and the ordering drops
   * to "As picked" — an automatic ordering would re-sort the section on the next cut
   * and throw the edit away, which would look like the drag simply failed.
   *
   * @param {SectionWellRow[]} next - The list in its new order.
   */
  const reorderWells = (next: SectionWellRow[]) => {
    setPicked(next.map(asPicked));
    setOrdering('as_selected');
  };

  /**
   * Puts chosen wells on the section: appended when adding, and in the standing-down
   * well's place when replacing, so the rest of the traverse keeps its positions.
   *
   * @param {PickedWell[]} chosen - The wells the picker committed.
   */
  const commitPicked = (chosen: PickedWell[]) => {
    if (chosen.length === 0) {
      setPicking(null);
      return;
    }
    if (picking?.mode === 'replace') {
      const outgoing = picking.well.uwi;
      // Substituting into the list as shown, so the replacement lands where the well it
      // stands in for was rather than at the end.
      setPicked(
        wells.flatMap((well) => {
          return well.uwi === outgoing ? chosen : [asPicked(well)];
        })
      );
      setOrdering('as_selected');
      setPicking(null);
      return;
    }
    // Adding leaves the ordering alone: appending asks no question about order, so an
    // ordering already chosen still answers it.
    setPicked((current) => [
      ...current,
      ...chosen.filter(
        (well) => !current.some((entry) => entry.uwi === well.uwi)
      ),
    ]);
    setPicking(null);
  };

  // The nearby sweep measures from the line the section actually has — the wells in this
  // mode, the drawn vertices in the other, and under an automatic ordering in the order
  // the backend put them, which is the line that is drawn. A well the picker just added
  // has no coordinates until the cut places it, so it cannot contribute to that line.
  const pickerVertices = useMemo(() => {
    if (wells.length === 0) {
      return drawnVertices;
    }
    return wells.flatMap((well) => {
      if (well.lat === null || well.lon === null) {
        return [];
      }
      return [{ lat: well.lat, lon: well.lon }];
    });
  }, [wells, drawnVertices]);

  // A well already on the section is not a candidate — except the one being stood down,
  // which the user may well want to put back.
  const pickerExcludes = useMemo(() => {
    const outgoing = picking?.mode === 'replace' ? picking.well.uwi : null;
    return wells.map((well) => well.uwi).filter((uwi) => uwi !== outgoing);
  }, [wells, picking]);

  // Any grid the section crosses is addable, not only the selected collection's. Asked
  // while the panel is open, not once the picker is: the button that opens the modal
  // counts these, so gating on the modal disabled it exactly when it was needed.
  const {
    grids: crossedGrids,
    answered: crossedAnswered,
    pending: crossedPending,
    loading: crossedLoading,
    error: crossedError,
    intersectingCollectionIds,
  } = useIntersectingStructureGrids(pickerVertices, collectionId, open);

  // Which collections have anything to say about THIS line, known only once the
  // crossed grids have landed. Null until then, and null for a section with no line
  // yet: an unanswered question must not read as an answer of none.
  const crossedCollectionIds = crossedAnswered
    ? intersectingCollectionIds
    : null;

  // Only the collections the section passes through, as UAT asked. Two exceptions, both
  // about not stranding the user: the selected collection stays whatever the line does,
  // since Carbon empties the field when its `selectedItem` is not among the items; and a
  // line that crosses nothing narrows to nothing, so the full list stands instead.
  const collectionItems = useMemo(() => {
    if (crossedCollectionIds === null || crossedCollectionIds.size === 0) {
      return collections;
    }
    return collections.filter(
      (collection) =>
        crossedCollectionIds.has(collection.id) ||
        collection.id === collectionId
    );
  }, [collections, crossedCollectionIds, collectionId]);

  const collectionRow = useCallback(
    (item: CollectionSummary | null) =>
      renderCollection(item, crossedCollectionIds),
    [crossedCollectionIds]
  );

  const candidates = useMemo(() => {
    // Until the line has been asked about — and for a section with no line yet — the
    // collection's own grids are the honest offer. An answered-but-empty line has
    // nothing to add, and says so through the picker's empty state.
    const offered = crossedAnswered ? crossedGrids : grids;
    return offered.filter((grid) => !gridIds.includes(grid.structureGridId));
  }, [crossedAnswered, crossedGrids, grids, gridIds]);

  // Whatever either list has said about a grid, kept so a grid from outside the selected
  // collection still has a row here to flatten or remove it by.
  useEffect(() => {
    remember([...grids, ...crossedGrids]);
  }, [grids, crossedGrids, remember]);

  if (!open) {
    return null;
  }

  return (
    <aside className={styles.panel} aria-label="Cross section settings">
      <div className={styles.header}>
        <div>
          <h3>Cross Section Settings</h3>
          <p>Configure cross-section inputs</p>
        </div>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={Close}
          iconDescription="Close"
          tooltipPosition="left"
          onClick={() => setOpen(false)}
        />
      </div>

      <div className={styles.body}>
        {/* Structure Grids lead: every section samples surfaces. */}
        <Accordion className={styles.accordion}>
          <AccordionItem
            open={gridsOpen}
            onHeadingClick={() => setGridsOpen(!gridsOpen)}
            title={
              <span className={styles.sectionTitle}>
                <span>Structure Grids</span>
                {onSection.length > 0 && (
                  <span
                    className={styles.count}
                  >{`(${onSection.length})`}</span>
                )}
              </span>
            }
          >
            <div className={styles.sectionBody}>
              {collectionsError && (
                <InlineNotification
                  kind="error"
                  lowContrast
                  hideCloseButton
                  title="Could not load collections"
                  subtitle={collectionsError.message}
                />
              )}
              <Dropdown
                id="cross-section-collection"
                size="sm"
                className={styles.collectionDropdown}
                titleText="Structure Grid Collection"
                label={collectionsLoading ? 'Loading…' : 'Select a collection'}
                items={collectionItems}
                selectedItem={selected}
                itemToString={(item) => item?.collectionName ?? ''}
                itemToElement={collectionRow}
                renderSelectedItem={collectionRow}
                onChange={({ selectedItem }) => {
                  if (selectedItem) {
                    selectCollection({
                      collectionId: selectedItem.id,
                      gridIds: [],
                    });
                  }
                }}
              />

              {gridsLoading && onSection.length === 0 ? (
                <InlineLoading description="Loading grids…" />
              ) : (
                <ol className={styles.gridList}>
                  {onSection.map((grid) => (
                    <li key={grid.structureGridId}>
                      <span className={styles.gridRow}>
                        <span className={styles.gridText}>
                          <span
                            className={styles.gridName}
                            title={grid.geologicInterval}
                          >
                            {grid.geologicInterval}
                          </span>
                          <span
                            className={styles.gridMeta}
                            title={gridProvenance(grid)}
                          >
                            {gridProvenance(grid)}
                          </span>
                        </span>
                      </span>
                      {/* Aligned left: the panel abuts the profile, so a
                          right-opening tip would spill over the plot. */}
                      <Tooltip
                        align="left"
                        label={
                          grid.structureGridId === datumGridId
                            ? 'Flattened — click to restore'
                            : `Flatten on ${grid.geologicInterval}`
                        }
                      >
                        <button
                          type="button"
                          className={
                            grid.structureGridId === datumGridId
                              ? styles.flattenOn
                              : styles.flatten
                          }
                          aria-pressed={grid.structureGridId === datumGridId}
                          onClick={() => toggleDatum(grid.structureGridId)}
                        >
                          {/* Flattening collapses the section onto this horizon,
                              which is the fold the glyph draws. Drawn at 20 rather
                              than 16: its dashed centre line is a 2/32 feature, and
                              at 16 the dashes fall below a device pixel. */}
                          <VerticalFold size={20} />
                        </button>
                      </Tooltip>
                      <Tooltip
                        align="left"
                        label={`Remove ${grid.geologicInterval}`}
                      >
                        <button
                          type="button"
                          className={styles.remove}
                          onClick={() => removeGrid(grid.structureGridId)}
                        >
                          <SubtractAlt size={16} />
                        </button>
                      </Tooltip>
                    </li>
                  ))}
                </ol>
              )}

              {/* At the panel's foot, under the list it appends to. The catalogue
                  opens as a modal so the grid table can keep its columns. */}
              <div className={styles.addGrid}>
                <Button
                  kind="tertiary"
                  size="sm"
                  renderIcon={Add}
                  // Shut only on a settled answer: while the line is still being asked
                  // about the door stays open and the picker says it is loading. Locking
                  // it on the collection's word alone made crossed grids unreachable.
                  disabled={!crossedPending && candidates.length === 0}
                  onClick={() => setAddingGrids(true)}
                >
                  {candidates.length > 0
                    ? `Add grid (${candidates.length})`
                    : crossedPending
                      ? 'Add grids'
                      : 'All grids added'}
                </Button>
              </div>
            </div>
          </AccordionItem>
        </Accordion>
        {/* Wells define the plane in this mode, so how they are ordered is a
            section input. Arbitrary Line keeps wells out of settings entirely. */}
        {mode === 'well_to_well' && (
          <Accordion className={styles.accordion}>
            <AccordionItem
              open={wellsOpen}
              onHeadingClick={() => setWellsOpen(!wellsOpen)}
              title={
                <span className={styles.sectionTitle}>
                  <span>Wells</span>
                  {wells.length > 0 && (
                    <span className={styles.count}>({wells.length})</span>
                  )}
                </span>
              }
            >
              <div className={styles.sectionBody}>
                <Dropdown
                  id="cross-section-well-ordering"
                  size="sm"
                  className={styles.collectionDropdown}
                  titleText="Order wells by"
                  label="As picked"
                  items={WELL_ORDERINGS}
                  selectedItem={
                    WELL_ORDERINGS.find((option) => option.id === ordering) ??
                    null
                  }
                  itemToString={(item) => item?.label ?? ''}
                  onChange={({ selectedItem }) => {
                    if (selectedItem) {
                      setOrdering(selectedItem.id);
                    }
                  }}
                />
                {wells.length < 2 && (
                  <p className={styles.hint}>
                    Click wells on the map, or add them below. Two or more make
                    a section.
                  </p>
                )}
                {/* The traverse, in the order the plane runs. The position is the key
                    the plot's guide and the map's numbered dot share, so one well can
                    be followed across all three. */}
                <SectionWellsList
                  wells={wells}
                  focusedWellUwi={focusedWellUwi}
                  onFocus={toggleFocus}
                  onRemove={(well) => removeWell(asPicked(well))}
                  onReplace={(well, position) =>
                    setPicking({ mode: 'replace', well, position })
                  }
                  onReorder={reorderWells}
                />
                {/* Wells can be put on the section from here as well as by clicking
                    the map, because a well outside the current view cannot be
                    clicked and a section is free to reach past one screenful. */}
                <Button
                  kind="tertiary"
                  size="sm"
                  renderIcon={Add}
                  className={styles.addWells}
                  onClick={() => setPicking({ mode: 'add' })}
                >
                  Add wells
                </Button>
              </div>
            </AccordionItem>
          </Accordion>
        )}
      </div>

      {addingGrids && (
        <AddGridsModal
          candidates={candidates}
          loading={crossedLoading}
          error={crossedError}
          onClose={() => setAddingGrids(false)}
          onAdd={(ids) => {
            addGrids(ids);
            setAddingGrids(false);
          }}
        />
      )}
      {picking !== null && (
        <WellPickerModal
          mode={picking.mode}
          replacing={
            picking.mode === 'replace'
              ? {
                  uwi: picking.well.uwi,
                  label: picking.well.name ?? picking.well.uwi,
                  position: picking.position,
                }
              : undefined
          }
          vertices={pickerVertices}
          excludeUwis={pickerExcludes}
          onClose={() => setPicking(null)}
          onCommit={commitPicked}
        />
      )}
    </aside>
  );
};
